import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import NavBar from '../components/NavBar';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';

const bgContent = {
  title: 'Политика за поверителност',
  lastUpdated: 'Последна актуализация: 22 април 2026 г.',
  sections: [
    {
      heading: '1. Администратор на лични данни',
      body: `Администратор на личните данни е Адвокатска кантора Петър Николов, с адрес: бул. "Цар Освободител" 44, ет. 4, офис 6, гр. Варна, България.\n\nЗа въпроси относно обработката на лични данни можете да се свържете с нас на:\n• Имейл: kantora@advokatnikolov.com\n• Телефон: +359 882 744 951`
    },
    {
      heading: '2. Какви лични данни събираме',
      body: `Чрез формата за контакт на сайта събираме следните данни, предоставени доброволно от Вас:\n• Име\n• Имейл адрес\n• Телефонен номер (по желание)\n• Съдържание на съобщението\n\nНе събираме чувствителни лични данни чрез уебсайта.`
    },
    {
      heading: '3. Цел и правно основание за обработка',
      body: `Обработваме Вашите лични данни за следните цели:\n• Отговор на Вашите запитвания и предоставяне на правна консултация — на основание легитимен интерес (чл. 6, ал. 1, буква „е" от GDPR) и предприемане на стъпки по Ваше искане преди сключване на договор (чл. 6, ал. 1, буква „б" от GDPR).\n• Анализ на посещаемостта на сайта чрез Google Analytics — на основание Вашето съгласие, управлявано чрез панела за настройки на бисквитките (чл. 6, ал. 1, буква „а" от GDPR).`
    },
    {
      heading: '4. Как обработваме данните от формата за контакт',
      body: `Данните от формата за контакт се изпращат чрез услугата EmailJS директно на имейл адреса на кантората. Данните не се съхраняват на сървърите на уебсайта. EmailJS действа като обработващ данни и предава информацията чрез криптирана връзка.\n\nПолучените запитвания се съхраняват в имейл кутията на кантората за срок до 3 години след приключване на комуникацията, освен ако не е налице правно основание за по-дълго съхранение.`
    },
    {
      heading: '5. Бисквитки (Cookies)',
      body: `Уебсайтът използва следните видове бисквитки:\n• Необходими бисквитки — за основна функционалност на сайта (езикова настройка чрез localStorage).\n• Аналитични бисквитки — Google Analytics за анализ на посещаемостта.\n\nПри първото Ви посещение ще бъдете помолени да дадете съгласие за използване на аналитични бисквитки. Можете да промените избора си по всяко време чрез връзката "Настройки за бисквитки" в долната част на сайта.`
    },
    {
      heading: '6. Предаване на данни на трети страни',
      body: `Данните Ви могат да бъдат предадени на следните трети страни, единствено за целите, описани в тази политика:\n• EmailJS (предаване на данни от формата за контакт)\n• Google Analytics (анализ на посещаемостта след изрично съгласие)\n\nНе продаваме, отдаваме под наем или споделяме Вашите лични данни за маркетингови цели.`
    },
    {
      heading: '7. Вашите права по GDPR',
      body: `Съгласно Общия регламент за защита на данните (GDPR), Вие имате следните права:\n• Право на достъп — да получите информация за обработваните Ви лични данни.\n• Право на коригиране — да поискате корекция на неточни данни.\n• Право на изтриване — да поискате изтриване на данните Ви ("право да бъдеш забравен").\n• Право на ограничаване на обработката.\n• Право на преносимост на данните.\n• Право на възражение срещу обработката.\n• Право да оттеглите съгласието си по всяко време (без това да засяга законосъобразността на обработката преди оттеглянето).\n\nЗа упражняване на Вашите права, моля свържете се с нас на kantora@advokatnikolov.com.`
    },
    {
      heading: '8. Право на жалба',
      body: `Ако считате, че обработката на личните Ви данни нарушава GDPR, имате право да подадете жалба до Комисията за защита на личните данни (КЗЛД):\n• Адрес: бул. "Проф. Цветан Лазаров" 2, гр. София 1592\n• Уебсайт: www.cpdp.bg\n• Имейл: kzld@cpdp.bg`
    },
    {
      heading: '9. Промени в политиката',
      body: 'Запазваме правото си да актуализираме тази политика за поверителност. Всички промени ще бъдат публикувани на тази страница с актуализирана дата.'
    }
  ]
};

const enContent = {
  title: 'Privacy Policy',
  lastUpdated: 'Last updated: April 22, 2026',
  sections: [
    {
      heading: '1. Data Controller',
      body: `The data controller is Petar Nikolov Law Firm, located at bul. "Tsar Osvoboditel" 44, floor 4, office 6, Varna, Bulgaria.\n\nFor questions regarding data processing, you can contact us at:\n• Email: kantora@advokatnikolov.com\n• Phone: +359 882 744 951`
    },
    {
      heading: '2. Personal Data We Collect',
      body: `Through the contact form on this website, we collect the following data provided voluntarily by you:\n• Name\n• Email address\n• Phone number (optional)\n• Message content\n\nWe do not collect sensitive personal data through the website.`
    },
    {
      heading: '3. Purpose and Legal Basis for Processing',
      body: `We process your personal data for the following purposes:\n• Responding to your inquiries and providing legal consultation — based on legitimate interest (Art. 6(1)(f) GDPR) and taking steps at your request prior to entering into a contract (Art. 6(1)(b) GDPR).\n• Analyzing website traffic through Google Analytics — based on your consent, managed through the website's cookie settings panel (Art. 6(1)(a) GDPR).`
    },
    {
      heading: '4. How We Process Contact Form Data',
      body: `Contact form data is sent via the EmailJS service directly to the firm's email address. Data is not stored on the website's servers. EmailJS acts as a data processor and transmits the information through an encrypted connection.\n\nReceived inquiries are stored in the firm's email inbox for up to 3 years after the communication concludes, unless there is a legal basis for longer retention.`
    },
    {
      heading: '5. Cookies',
      body: `This website uses the following types of cookies:\n• Necessary cookies — for basic site functionality (language preference via localStorage).\n• Analytics cookies — Google Analytics for traffic analysis.\n\nOn your first visit, you will be asked to consent to analytics cookies. You can change your choice at any time through the "Cookie settings" link in the website footer.`
    },
    {
      heading: '6. Data Sharing with Third Parties',
      body: `Your data may be shared with the following third parties, solely for the purposes described in this policy:\n• EmailJS (contact form data transmission)\n• Google Analytics (traffic analysis after explicit consent)\n\nWe do not sell, rent, or share your personal data for marketing purposes.`
    },
    {
      heading: '7. Your Rights Under GDPR',
      body: `Under the General Data Protection Regulation (GDPR), you have the following rights:\n• Right of access — to obtain information about your processed personal data.\n• Right to rectification — to request correction of inaccurate data.\n• Right to erasure — to request deletion of your data ("right to be forgotten").\n• Right to restriction of processing.\n• Right to data portability.\n• Right to object to processing.\n• Right to withdraw consent at any time (without affecting the lawfulness of processing prior to withdrawal).\n\nTo exercise your rights, please contact us at kantora@advokatnikolov.com.`
    },
    {
      heading: '8. Right to Lodge a Complaint',
      body: `If you believe that the processing of your personal data violates the GDPR, you have the right to lodge a complaint with the Commission for Personal Data Protection (CPDP):\n• Address: bul. "Prof. Tsvetan Lazarov" 2, Sofia 1592, Bulgaria\n• Website: www.cpdp.bg\n• Email: kzld@cpdp.bg`
    },
    {
      heading: '9. Changes to This Policy',
      body: 'We reserve the right to update this privacy policy. Any changes will be published on this page with an updated date.'
    }
  ]
};

function PrivacyPolicyContent() {
  const { language } = useLanguage();
  const content = language === 'bg' ? bgContent : enContent;

  React.useEffect(() => {
    document.body.setAttribute('data-lang', language);
    document.documentElement.setAttribute('lang', language === 'bg' ? 'bg' : 'en');
    window.scrollTo(0, 0);
  }, [language]);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{language === 'bg' ? 'Политика за поверителност | Адвокат Петър Николов' : 'Privacy Policy | Attorney Petar Nikolov'}</title>
        <meta name="description" content={language === 'bg' ? 'Политика за поверителност на Адвокатска кантора Петър Николов. Информация за обработката на лични данни и Вашите права по GDPR.' : 'Privacy policy of Petar Nikolov Law Firm. Information about personal data processing and your rights under GDPR.'} />
        <link rel="canonical" href="https://advokatnikolov.com/privacy-policy" />
      </Helmet>
      <NavBar />
      <section className="pt-32 pb-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-serif text-navy mb-2">{content.title}</h1>
          <p className="text-sm text-gray-400 mb-12">{content.lastUpdated}</p>
          {content.sections.map((section, i) => (
            <div key={i} className="mb-10">
              <h2 className="text-xl font-serif font-semibold text-navy mb-3">{section.heading}</h2>
              <div className="text-slate leading-relaxed whitespace-pre-line">{section.body}</div>
            </div>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}

export default PrivacyPolicyContent;
